import { useEffect, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Search, Warehouse } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { formatDateTime } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill, type Tone } from "@/components/ui/StatusPill";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { Drawer, Modal } from "@/components/ui/Modal";
import { toast } from "@/components/ui/toast";
import { EmptyState, TableSkeleton } from "@/components/ui/states";
import {
  ADJUST_REASONS,
  MOVEMENT_LABEL,
  adjustInventory,
  fetchInventory,
  fetchMovements,
  type StockFilter,
  type StockRow,
} from "./api";

const PAGE_SIZE = 30;

const STATUS: Record<string, { label: string; tone: Tone }> = {
  ok: { label: "Normal", tone: "ok" },
  low: { label: "Stock bajo", tone: "warn" },
  out_of_stock: { label: "Agotado", tone: "crit" },
};

const FILTERS: { id: StockFilter; label: string }[] = [
  { id: "all", label: "Todo" },
  { id: "low", label: "Stock bajo" },
  { id: "out", label: "Agotados" },
];

export function InventoryPage() {
  const { org } = useActiveOrg();
  const [filter, setFilter] = useState<StockFilter>("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [adjusting, setAdjusting] = useState<StockRow | null>(null);
  const [viewing, setViewing] = useState<StockRow | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => setPage(0), [filter, debounced, org?.organizationId]);

  const query = useQuery({
    queryKey: ["inventory", org?.organizationId, filter, debounced, page],
    queryFn: () =>
      fetchInventory({
        organizationId: org!.organizationId,
        filter,
        search: debounced,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: Boolean(org),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-head font-semibold tracking-tight">Inventario</h1>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Producto o SKU"
            aria-label="Buscar en inventario"
            className="w-full rounded-md border border-line bg-surface py-1.5 pl-8 pr-3 text-ui sm:w-64"
          />
        </div>
      </header>

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              filter === f.id
                ? "border-ink bg-ink text-ground"
                : "border-line bg-surface text-ink-2 hover:bg-sunk",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Panel bleed>
        <QueryBoundary
          query={query}
          skeleton={<TableSkeleton rows={8} cols={5} />}
          isEmpty={(d) => d.rows.length === 0}
          empty={
            <EmptyState
              icon={<Warehouse className="size-5" />}
              title={filter === "all" ? "Sin productos en el almacén" : "Nada que revisar"}
              description={
                filter === "all"
                  ? "Cuando cargues productos con stock aparecerán aquí."
                  : "No hay variantes en este estado ahora mismo."
              }
            />
          }
        >
          {(data) => (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] border-collapse text-ui">
                  <thead>
                    <tr>
                      {["Producto", "SKU", "Disponible", "Reservado", "Estado", ""].map((h, i) => (
                        <th
                          key={h || i}
                          className={cn(
                            "border-b border-line bg-sunk px-4 py-2.5 font-mono text-mark font-medium uppercase tracking-[0.1em] text-ink-3",
                            i === 2 || i === 3 ? "text-right" : "text-left",
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => {
                      const s = STATUS[r.stock_status ?? "ok"] ?? STATUS.ok!;
                      return (
                        <tr key={r.variant_id} className="border-b border-line-2 last:border-0">
                          <td className="px-4 py-3">
                            <span className="font-medium">{r.product_name}</span>
                            <span className="block text-xs text-ink-3">{r.variant_name}</span>
                          </td>
                          <td className="tnum px-4 py-3 text-xs text-ink-3">{r.sku}</td>
                          <td className="tnum px-4 py-3 text-right font-semibold">
                            {r.quantity_available}
                          </td>
                          <td className="tnum px-4 py-3 text-right text-ink-3">
                            {r.quantity_reserved}
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill tone={s.tone}>{s.label}</StatusPill>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setViewing(r)}>
                                <History className="size-3.5" />
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => setAdjusting(r)}>
                                Ajustar
                              </Button>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-line-2 px-4 py-2.5">
                <span className="tnum text-xs text-ink-3">
                  {data.total} variante{data.total === 1 ? "" : "s"}
                </span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={(page + 1) * PAGE_SIZE >= data.total}
                    onClick={() => setPage(page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </QueryBoundary>
      </Panel>

      <AdjustDialog row={adjusting} onClose={() => setAdjusting(null)} />
      <MovementsDrawer row={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

/* ------------------------------------------------------------------ ajuste */

function AdjustDialog({ row, onClose }: { row: StockRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState(ADJUST_REASONS[0]!);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row) setQuantity(row.quantity_available ?? 0);
  }, [row]);

  if (!row) return null;

  const current = row.quantity_available ?? 0;
  const delta = quantity - current;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Ajustar ${row.product_name}`}
      description={
        <>
          {row.variant_name} · <span className="tnum">{row.sku}</span>. Indica la cantidad
          que hay <strong className="text-ink">ahora mismo</strong> en el almacén, no la
          diferencia.
        </>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={delta === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await adjustInventory({
                  organizationId: row.organization_id!,
                  warehouseId: row.warehouse_id!,
                  variantId: row.variant_id!,
                  newQuantity: quantity,
                  reason,
                });
                toast.ok(`Stock actualizado: ${current} → ${quantity}`);
                void qc.invalidateQueries({ queryKey: ["inventory"] });
                onClose();
              } catch (e) {
                toast.error(friendlyError(e));
              }
              setBusy(false);
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setQuantity((q) => Math.max(0, q - 1))}
            aria-label="Restar uno"
          >
            −
          </Button>
          <input
            value={quantity}
            onChange={(e) => setQuantity(Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0))}
            inputMode="numeric"
            aria-label="Cantidad final"
            className="tnum w-24 rounded-md border border-line bg-surface px-3 py-2 text-center text-lg font-semibold"
          />
          <Button variant="secondary" onClick={() => setQuantity((q) => q + 1)} aria-label="Sumar uno">
            +
          </Button>
        </div>

        <p className="text-center text-xs text-ink-3">
          Ahora hay <span className="tnum">{current}</span>
          {delta !== 0 && (
            <>
              {" · "}
              <span className={cn("tnum font-medium", delta > 0 ? "text-ok" : "text-crit")}>
                {delta > 0 ? "+" : ""}
                {delta}
              </span>
            </>
          )}
        </p>

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium">Motivo</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded-md border border-line bg-surface px-3 py-2 text-ui"
          >
            {ADJUST_REASONS.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
          <span className="text-xs text-ink-3">
            Queda registrado en el historial junto a quién lo hizo.
          </span>
        </label>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------- historial */

function MovementsDrawer({ row, onClose }: { row: StockRow | null; onClose: () => void }) {
  const { org } = useActiveOrg();
  const query = useQuery({
    queryKey: ["movements", row?.variant_id],
    queryFn: () => fetchMovements(row!.variant_id!),
    enabled: Boolean(row?.variant_id),
  });

  if (!row) return null;

  return (
    <Drawer open onClose={onClose} title={`${row.product_name} · ${row.variant_name}`}>
      <QueryBoundary
        query={query}
        skeleton={<TableSkeleton rows={5} cols={2} />}
        isEmpty={(d) => d.length === 0}
        empty={<EmptyState title="Sin movimientos" description="Esta variante aún no tiene historial." />}
      >
        {(rows) => (
          <ol className="flex flex-col gap-3">
            {rows.map((m) => (
              <li key={m.id} className="flex items-start justify-between gap-3 text-ui">
                <span className="min-w-0">
                  <span className="block font-medium">
                    {MOVEMENT_LABEL[m.movement_type] ?? m.movement_type}
                  </span>
                  {(m.reason || m.notes) && (
                    <span className="block text-xs text-ink-3">{m.reason ?? m.notes}</span>
                  )}
                  <span className="tnum block text-xs text-ink-3">
                    {formatDateTime(m.created_at, org?.timezone)}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end">
                  <span
                    className={cn(
                      "tnum font-semibold",
                      m.quantity > 0 ? "text-ok" : m.quantity < 0 ? "text-crit" : "text-ink-3",
                    )}
                  >
                    {m.quantity > 0 ? "+" : ""}
                    {m.quantity}
                  </span>
                  <span className="tnum text-xs text-ink-3">queda {m.resulting_available}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </QueryBoundary>
    </Drawer>
  );
}
